<template>
  <article class="wx-article" :lang="isEnglish ? 'en' : 'zh-CN'">
    <div class="wx-inner">
      <h1 class="wx-title">{{ article.title }}</h1>
      <template v-for="(block, index) in article.blocks" :key="index">
        <div
          v-if="block.type === 'image'"
          class="wx-media"
          :class="block.align === 'center' ? 'is-center' : 'is-left'"
          :style="{ marginBottom: block.mb + 'px' }"
        >
          <img
            :src="isEnglish && block.srcEn ? block.srcEn : block.src"
            :alt="article.title"
            :class="{ 'is-full': !block.width }"
            :style="block.width ? { width: block.width + 'px' } : null"
            loading="lazy"
          />
        </div>
        <div v-else-if="block.type === 'banner'" class="wx-banner" :style="blockStyle(block)">
          <span v-for="(line, li) in block.lines" :key="li" class="wx-banner-line">
            <i v-if="li === 0" class="wx-banner-dot" aria-hidden="true"></i><span v-for="(seg, si) in line" :key="si" :class="{ 'wx-banner-hl': seg.h }">{{ seg.t }}</span>
          </span>
        </div>
        <div v-else-if="block.type === 'qrcard'" class="wx-qrcard" :style="blockStyle(block)">
          <div class="wx-qrcard-left">
            <span class="wx-qrcard-label">{{ block.label }}<sup>®</sup></span>
            <span class="wx-qrcard-scan">{{ block.scan }}</span>
          </div>
          <div class="wx-qrcard-mid">
            <strong class="wx-qrcard-title">{{ block.title }}</strong>
            <span class="wx-qrcard-sub">{{ block.sub }}</span>
          </div>
          <img class="wx-qrcard-qr" :src="block.qr" :alt="block.title" loading="lazy" />
        </div>
        <p v-else-if="block.type === 'blank'" class="wx-blank" :style="blockStyle(block)"><br /></p>
        <p v-else-if="block.type === 'p'" class="wx-p" :style="blockStyle(block)">
          <span v-for="(seg, si) in block.segs" :key="si" :class="seg.c">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
        <component
          :is="block.ordered === false ? 'ul' : 'ol'"
          v-else-if="block.type === 'list'"
          class="wx-list"
        >
          <li v-for="(item, ii) in block.items" :key="ii">
            <p class="wx-p">
              <span v-for="(seg, si) in item" :key="si" :class="seg.c">
                <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
                <template v-else>{{ seg.t }}</template>
              </span>
            </p>
          </li>
        </component>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import qrProteinn1 from "../../assets/image/qr-proteinn-1.png";
import qrProteinn2 from "../../assets/image/qr-proteinn-2.png";

const { locale } = useI18n();
const isEnglish = computed(() => locale.value === "en");

const content = {
  "zh": {
    "title": "Ampitor® 35 无搅拌，0 剪切! Cell-Free DNA/RNA AMPlification 无细胞体外 DNA/RNA 合成、模板扩增",
    "blocks": [
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P1.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P2.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P2.jpg",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "AMPitor 35，基于康码",
              "h": true
            },
            {
              "t": "全球领先的百"
            }
          ],
          [
            {
              "t": "吨级 D2P",
              "h": true
            },
            {
              "t": " 无细胞蛋白质合成技术，用于"
            }
          ],
          [
            {
              "t": "体外大量扩增DNA/RNA，等",
              "h": true
            },
            {
              "t": "多种用途"
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "基于康码全球领先的 D2P (DNA to Protein) 无细胞蛋白质合成技术，康码自主研发了 6 通量模板扩增仪 Ampitor 35，可快速实现 DNA/RNA 分子的大量体外无细胞扩增，适配于升级无细胞蛋白合成反应，更可用于核酸 DNA/RNA 制备等多种用途。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P4.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P4.jpg",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "康码 AMPitor 35，拥有 6X 独立通道，"
            }
          ],
          [
            {
              "t": "结合全新 D2P 技术专利，可同时扩增 6 种"
            }
          ],
          [
            {
              "t": "不同 DNA / RNA 模板"
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "康码 AMPitor 35，拥有 6X 独立通道，结合全新 D2P 技术专利，可同时扩增 6 种不同 DNA / RNA 模板，每个通道扩增高达 35mL DNA 模板。每通道扩增的 DNA 模板足够用于 1000mL 体外蛋白质合成反应。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-2.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-2.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "搭配专为 ProteinFactory 平台设计的 DNA 模板扩增试剂盒 AMPiX，利用 DNA 滚环扩增原理，可以将 DNA 模板扩增 1000000 倍以上。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-3.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-3.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "微量",
            "c": "b"
          },
          {
            "t": "，最少只需要 1000 个 DNA 模板分子，1ng DNA。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "广谱",
            "c": "b"
          },
          {
            "t": "，可扩增环状 DNA，或线性化 PCR 片段。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "快速",
            "c": "b"
          },
          {
            "t": "，37℃ 环境下，最快只需 1 小时，即可充分扩增 DNA 模板。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "方便",
            "c": "b"
          },
          {
            "t": "，只需加水和模板 DNA，混匀后即可进行扩增。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-4.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-4.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "qrcard",
        "label": "PROTEINN",
        "scan": "扫一扫 进入",
        "title": "蛋白质工厂小程序",
        "sub": "了解更多产品信息",
        "qr": qrProteinn1,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "搭配全新 ProteinFactory FastLiter 产品使用，快速蛋白质合成，更高得率，更有活性，高通量，轻松合成。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P7-1.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P7-1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "qrcard",
        "label": "PROTEINN",
        "scan": "扫一扫 进入",
        "title": "蛋白质工厂小程序",
        "sub": "了解更多产品信息",
        "qr": qrProteinn2,
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "康码 AMPitor 35 的下游应用："
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "包括 DNA 原位扩增，RNA 转录反应，蛋白质体外合成等多种应用需求，每个反应体积最大可达 35mL, 6X 独立通道，总计 210 mL, 配合下游 ProteinFactory FastLiter 产品使用，可一次支持6000 mL的无细胞蛋白质合成反应，最高可以合成 12g 的目标蛋白（以序列不同而异）。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P10.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P10.png",
        "width": null,
        "align": "center",
        "mb": 24
      }
    ]
  },
  "en": {
    "title": "Ampitor® 35 — No Stirring, Zero Shearing! Cell-Free DNA/RNA AMPlification: In Vitro DNA/RNA Synthesis and Template Amplification",
    "blocks": [
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P1.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P2.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P2.jpg",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "AMPitor 35",
              "h": true
            },
            {
              "t": " — built on Healthcode's globally leading hundred-ton-scale "
            },
            {
              "t": "D2P",
              "h": true
            },
            {
              "t": " cell-free protein synthesis technology, for large-scale in vitro amplification of "
            },
            {
              "t": "DNA/RNA",
              "h": true
            },
            {
              "t": " and many other applications."
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Built on Healthcode's globally leading D2P (DNA to Protein) cell-free protein synthesis technology, Healthcode independently developed the 6-throughput template amplifier Ampitor 35. It rapidly performs large-scale in vitro cell-free amplification of DNA/RNA molecules, is suitable for upgrading cell-free protein synthesis reactions, and can also be used for nucleic acid DNA/RNA preparation and many other applications.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P4.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P4.jpg",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "The Healthcode AMPitor 35 features 6X independent channels; combined with the new D2P patented technology, it can amplify 6 different DNA / RNA templates simultaneously."
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Healthcode AMPitor 35 features 6 independent channels and incorporates the new patented D2P technology, amplifying up to 6 different DNA/RNA templates simultaneously, with each channel amplifying up to 35 mL of DNA template. The DNA template amplified in each channel is enough for a 1000 mL in vitro protein synthesis reaction.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-2.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-2.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Paired with AMPiX, a DNA template amplification kit designed specifically for the ProteinFactory platform, it uses the DNA rolling-circle amplification principle to amplify DNA templates more than 1,000,000-fold.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-3.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-3.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Micro-scale",
            "c": "b"
          },
          {
            "t": ", requiring as few as 1,000 DNA template molecules, or 1 ng of DNA.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Broad-spectrum",
            "c": "b"
          },
          {
            "t": ", capable of amplifying circular DNA or linearized PCR fragments.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Fast",
            "c": "b"
          },
          {
            "t": " — at 37 °C it takes as little as 1 hour to fully amplify the DNA template.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Convenient",
            "c": "b"
          },
          {
            "t": " — simply add water and template DNA, mix, and amplification can proceed.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P5-4.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P5-4.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "qrcard",
        "label": "PROTEINN",
        "scan": "Scan to enter",
        "title": "Protein Factory",
        "sub": "Explore our products",
        "qr": qrProteinn1,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Used together with the new ProteinFactory FastLiter product, it enables rapid protein synthesis with higher yield and higher activity, high throughput, and effortless synthesis.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P7-1.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P7-1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "qrcard",
        "label": "PROTEINN",
        "scan": "Scan to enter",
        "title": "Protein Factory",
        "sub": "Explore our products",
        "qr": qrProteinn2,
        "mb": 24
      },
      {
        "type": "banner",
        "lines": [
          [
            {
              "t": "Downstream applications of the Healthcode AMPitor 35:"
            }
          ]
        ],
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "It supports a wide range of application needs including in situ DNA amplification, RNA transcription reactions and in vitro protein synthesis. Each reaction volume can reach up to 35 mL, and with 6 independent channels the total reaches 210 mL; combined with the downstream ProteinFactory FastLiter product, it can support a 6000 mL cell-free protein synthesis reaction in one go, producing up to 12 g of target protein (depending on the sequence).",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/Ampitor35/P10.png",
        "srcEn": "https://www.healthcode.com/media/Ampitor35/en/P10.png",
        "width": null,
        "align": "center",
        "mb": 24
      }
    ]
  }
};

// 还原原文里显式写出的上下间距（空行块、部分段落）
const blockStyle = (block) => ({
  marginTop: block.mt !== undefined ? block.mt + "px" : null,
  marginBottom: block.mb !== undefined ? block.mb + "px" : null,
});

const article = computed(() => (isEnglish.value ? content.en : content.zh));
</script>

<style scoped>
.wx-article {
  width: 100%;
  min-height: 100vh;
  padding: 8rem 0 4rem;
  background: #fff;
}

.wx-inner {
  max-width: 677px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: 0.034em;
  color: #333;
  text-align: justify;
  overflow-wrap: break-word;
}

.wx-title {
  margin: 0 0 24px;
  color: #222;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.4;
  text-align: left;
}

.wx-p {
  margin: 0 0 16px;
}

/* 原文用 <br> / &nbsp; 撑出的空行，保留一个行高的留白 */
.wx-blank {
  margin: 0;
  line-height: 1.75;
}

/* 标题横幅：低清图片改为 HTML 文字还原（蓝点 + 关键词浅蓝高亮） */
.wx-banner {
  margin: 0 0 16px;
  text-align: left;
  font-size: 27px;
  font-weight: 600;
  line-height: 1.95;
  letter-spacing: 0.02em;
  color: #000;
}

.wx-banner-line {
  display: block;
}

.wx-banner-dot {
  display: inline-block;
  width: 0.82em;
  height: 0.82em;
  margin-right: 0.4em;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 32%, #8fc3f5, #1d5db6 72%);
  vertical-align: 0.02em;
}

.wx-banner-hl {
  background-image: linear-gradient(transparent 62%, #dbe8fb 62%);
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}

/* 小程序卡片：低清图片改为 HTML 还原（文字部分），二维码使用高清裁剪图 */
.wx-qrcard {
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin: 0 0 24px;
  padding: 16px 20px;
  background: #f5f6f7;
  border-radius: 12px;
}

.wx-qrcard-left {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-start;
}

.wx-qrcard-label {
  background: #55565a;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.05em;
  line-height: 1;
  padding: 10px 14px;
  border-radius: 6px;
}

.wx-qrcard-label sup {
  font-size: 9px;
  margin-left: 1px;
}

.wx-qrcard-scan {
  margin-top: 38px;
  color: #444;
  font-size: 16px;
  line-height: 1;
}

.wx-qrcard-mid {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  min-width: 0;
}

.wx-qrcard-title {
  color: #333;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.35;
}

.wx-qrcard-sub {
  color: #808080;
  font-size: 17px;
  line-height: 1.4;
}

.wx-qrcard-qr {
  flex: 0 0 auto;
  align-self: center;
  width: 150px;
  height: 150px;
}

.wx-media {
  margin: 0 0 16px;
}

.wx-media.is-center {
  text-align: center;
}

.wx-media.is-left {
  text-align: left;
}

.wx-media img {
  display: inline-block;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.wx-media img.is-full {
  width: 100%;
}

.wx-list {
  margin: 0 0 16px;
  padding-left: 1.75em;
  list-style-type: decimal;
}

.wx-list li > .wx-p {
  margin-bottom: 8px;
}

.wx-list li:last-child > .wx-p {
  margin-bottom: 0;
}

.hl {
  color: rgb(0, 128, 255);
  font-weight: bold;
}

.wx-inner a {
  color: inherit;
  text-decoration: inherit;
}

.b {
  font-weight: bold;
}

.u {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .wx-article {
    padding-top: 7rem;
  }

  .wx-inner {
    font-size: 16px;
  }

  .wx-title {
    font-size: 19px;
  }

  .wx-banner {
    font-size: 21px;
  }

  .wx-qrcard {
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
  }

  .wx-qrcard-label {
    font-size: 13px;
    padding: 8px 11px;
  }

  .wx-qrcard-scan {
    margin-top: 24px;
    font-size: 14px;
  }

  .wx-qrcard-title {
    font-size: 20px;
  }

  .wx-qrcard-sub {
    font-size: 13px;
  }

  .wx-qrcard-qr {
    width: 112px;
    height: 112px;
  }
}
</style>
